import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPaperPlane, faCogs, faBell, faChartLine, 
  faExclamationTriangle, faRefresh, faPlus, faUser,
  faCheckCircle, faTimesCircle, faEye, faFileAlt,
  faBoxes, faClock, faThumbsUp, faThumbsDown,
  faCalendarAlt, faFilter, faUsers
} from '@fortawesome/free-solid-svg-icons';

import { useAuth } from '../auth/AuthContext';
import api from '../services/api';
import Navbar from '../components/layout/Navbar';
import LoadingSpinner from '../components/ui/LoadingSpinner';

interface PendingItem {
  _id: string;
  title: string;
  supplier: {
    name: string;
    email: string;
  };
  submittedAt: string;
  type: 'questionnaire' | 'article';
  status: 'submitted' | 'pending_review';
  ekArea?: string;
}

interface DashboardStats {
  totalSuppliers: number;
  activeSuppliers: number;
  pendingQuestionnaires: number;
  pendingArticles: number;
  newArticles: number;
  totalSubmissions: number;
  overdueItems: number;
}

// Statische Farb-Maps, damit Tailwind die Klassen beim Build nicht entfernt (kein dynamisches bg-${color})
type AccentColor = 'blue' | 'orange' | 'purple' | 'green' | 'red';

const ACCENT: Record<AccentColor, { soft: string; icon: string; solid: string; ring: string }> = {
  blue:   { soft: 'bg-blue-50',   icon: 'text-blue-600',   solid: 'bg-blue-500',   ring: 'ring-blue-100' },
  orange: { soft: 'bg-orange-50', icon: 'text-orange-600', solid: 'bg-orange-500', ring: 'ring-orange-100' },
  purple: { soft: 'bg-purple-50', icon: 'text-purple-600', solid: 'bg-purple-500', ring: 'ring-purple-100' },
  green:  { soft: 'bg-green-50',  icon: 'text-green-600',  solid: 'bg-green-500',  ring: 'ring-green-100' },
  red:    { soft: 'bg-red-50',    icon: 'text-red-600',    solid: 'bg-red-500',    ring: 'ring-red-100' },
};

const Dashboard: React.FC = () => {
  const { 
    isAdmin, 
    userEKAreas, 
    hasMenuPermission, 
    hasEKPermission,
    canAccessSubmissions,
    canAccessQuestionnaires,
    canAccessSuppliers,
    canAccessArticles,
    accessToken
  } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [stats, setStats] = useState<DashboardStats>({
    totalSuppliers: 0,
    activeSuppliers: 0,
    pendingQuestionnaires: 0,
    pendingArticles: 0,
    newArticles: 0,
    totalSubmissions: 0,
    overdueItems: 0
  });
  
  const [pendingQuestionnaires, setPendingQuestionnaires] = useState<PendingItem[]>([]);
  const [pendingArticles, setPendingArticles] = useState<PendingItem[]>([]);
  const [newArticles, setNewArticles] = useState<PendingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEKFilter, setSelectedEKFilter] = useState<string>('all');

  // Available EK areas for filter
  const availableEKAreas = isAdmin ? 
    ['EK0', 'EK1', 'EK2', 'EK3', 'EK4', 'EK5', 'EK6', 'EK7', 'EK8', 'EK9', 'EKV', 'EKD', 'EKW', 'EKG', 'EKSG'] :
    userEKAreas;

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build EK filter params
      const ekParams = new URLSearchParams();
      if (selectedEKFilter !== 'all') {
        ekParams.append('ekArea', selectedEKFilter);
      } else if (!isAdmin && userEKAreas.length > 0) {
        ekParams.append('ekAreas', userEKAreas.join(','));
      }

      // Parallel data loading with authenticated api calls
      const [statsRes, questionnaireRes, articleRes, newArticleRes] = await Promise.allSettled([
        api.get(`/admin/dashboard/stats?${ekParams}`),
        api.get(`/admin/dashboard/pending-questionnaires?${ekParams}`),
        api.get(`/admin/dashboard/pending-articles?${ekParams}`),
        api.get(`/admin/dashboard/new-articles?${ekParams}`)
      ]);

      // Process responses
      if (statsRes.status === 'fulfilled') {
        setStats(statsRes.value.data);
      }

      if (questionnaireRes.status === 'fulfilled') {
        setPendingQuestionnaires(questionnaireRes.value.data.questionnaires || []);
      }

      if (articleRes.status === 'fulfilled') {
        setPendingArticles(articleRes.value.data.articles || []);
      }

      if (newArticleRes.status === 'fulfilled') {
        setNewArticles(newArticleRes.value.data.articles || []);
      }    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Fehler beim Laden der Dashboard-Daten');
    } finally {
      setLoading(false);
    }
  }, [selectedEKFilter, isAdmin, userEKAreas]);

  useEffect(() => {
    if (userEKAreas.length > 0 || isAdmin) {
      loadDashboardData();
    } else {
      setLoading(false);
    }
  }, [loadDashboardData, userEKAreas, isAdmin]);

  // Action handlers
  const handleApproveQuestionnaire = async (id: string) => {
    try {
      const response = await fetch(`/api/admin/dashboard/questionnaires/${id}/approve`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      if (response.ok) {
        loadDashboardData();
      }
    } catch (error) {
      console.error('Error approving questionnaire:', error);
    }
  };

  const handleRejectQuestionnaire = async (id: string) => {
    try {
      const response = await fetch(`/api/admin/dashboard/questionnaires/${id}/reject`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      if (response.ok) {
        loadDashboardData();
      }
    } catch (error) {
      console.error('Error rejecting questionnaire:', error);
    }
  };

  const handleApproveArticle = async (id: string) => {
    try {
      const response = await fetch(`/api/admin/dashboard/articles/${id}/approve`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      if (response.ok) {
        loadDashboardData();
      }
    } catch (error) {
      console.error('Error approving article:', error);
    }
  };

  const handleRejectArticle = async (id: string) => {
    try {
      const response = await fetch(`/api/admin/dashboard/articles/${id}/reject`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      if (response.ok) {
        loadDashboardData();
      }
    } catch (error) {
      console.error('Error rejecting article:', error);
    }
  };

  // Quick actions based on permissions
  const quickActions = useMemo(() => {
    const actions = [];

    if (canAccessSubmissions) {
      actions.push({
        title: 'Neue Versendung',
        description: 'Fragebögen versenden',
        icon: faPaperPlane,
        color: 'purple',
        action: () => navigate('/submissions?action=bulk-send')
      });
    }

    if (canAccessSuppliers) {
      actions.push({
        title: 'Lieferanten',
        description: 'Lieferanten verwalten',
        icon: faUser,
        color: 'blue',
        action: () => navigate('/suppliers')
      });
    }

    if (canAccessArticles) {
      actions.push({
        title: 'Artikel',
        description: 'Artikel verwalten',
        icon: faBoxes,
        color: 'green',
        action: () => navigate('/articles')
      });
    }

    if (isAdmin) {
      actions.push({
        title: 'Analytics',
        description: 'Berichte und Analysen',
        icon: faChartLine,
        color: 'orange',
        action: () => navigate('/analytics')
      });
    }

    return actions;
  }, [canAccessSubmissions, canAccessSuppliers, canAccessArticles, isAdmin, navigate]);

  if (loading) {
    return <LoadingSpinner size="medium" text="Dashboard wird geladen..." />;
  }

  // No permissions view
  if (userEKAreas.length === 0 && !isAdmin) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <FontAwesomeIcon icon={faUser} className="text-6xl text-gray-300 mb-4" />
            <h3 className="text-xl font-medium text-gray-500 mb-2">Keine Berechtigung</h3>
            <p className="text-gray-400">
              Sie benötigen eine EK-Gruppenberechtigung oder Admin-Rechte für das Dashboard.
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <div className="bg-white border-b border-slate-200/70">
          <div className="px-6 py-5 max-w-[1600px] mx-auto w-full">
            <div className="flex items-center justify-end gap-4">
              {/* EK Filter */}
              <div className="flex items-center gap-2">
                <div className="relative">
                  <FontAwesomeIcon
                    icon={faFilter}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs pointer-events-none"
                  />
                  <select
                    value={selectedEKFilter}
                    onChange={(e) => setSelectedEKFilter(e.target.value)}
                    className="appearance-none pl-8 pr-9 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 shadow-sm hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-400 transition-colors cursor-pointer"
                  >
                    <option value="all">Alle EK-Bereiche</option>
                    {availableEKAreas.map(ek => (
                      <option key={ek} value={ek}>{ek}</option>
                    ))}
                  </select>
                  <svg
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                <button
                  onClick={loadDashboardData}
                  className="w-10 h-10 flex items-center justify-center text-slate-500 bg-white border border-slate-200 rounded-xl shadow-sm hover:text-slate-700 hover:border-slate-300 transition-colors"
                  title="Aktualisieren"
                >
                  <FontAwesomeIcon icon={faRefresh} />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 max-w-[1600px] mx-auto w-full">
          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6"
            >
              <div className="flex items-center">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mr-2" />
                <p className="text-red-800">{error}</p>
                <button
                  onClick={loadDashboardData}
                  className="ml-auto px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                >
                  Erneut versuchen
                </button>
              </div>
            </motion.div>
          )}

          {/* Quick Actions */}
          {quickActions.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6"
            >
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Schnellaktionen</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {quickActions.map((action, index) => {
                  const accent = ACCENT[action.color as AccentColor];
                  return (
                    <motion.button
                      key={action.title}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.08 }}
                      whileHover={{ y: -4 }}
                      onClick={action.action}
                      className="group flex items-center gap-4 p-4 bg-white rounded-2xl border border-slate-200/70 shadow-sm hover:shadow-md hover:border-slate-300 transition-all text-left"
                    >
                      <div className={`w-12 h-12 shrink-0 rounded-xl ${accent.soft} flex items-center justify-center transition-transform group-hover:scale-105`}>
                        <FontAwesomeIcon icon={action.icon} className={`${accent.icon} text-lg`} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-slate-900 truncate">{action.title}</h3>
                        <p className="text-sm text-slate-500 truncate">{action.description}</p>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            {([
              { label: 'Aktive Lieferanten', value: stats.activeSuppliers, icon: faUsers, color: 'blue' as AccentColor },
              { label: 'Wartende Fragebögen', value: stats.pendingQuestionnaires, icon: faClock, color: 'orange' as AccentColor },
              { label: 'Wartende Artikel', value: stats.pendingArticles, icon: faBoxes, color: 'purple' as AccentColor },
              { label: 'Neue Artikel', value: stats.newArticles, icon: faPlus, color: 'green' as AccentColor },
              { label: 'Überfällige Items', value: stats.overdueItems, icon: faExclamationTriangle, color: 'red' as AccentColor },
            ]).map((stat, index) => {
              const accent = ACCENT[stat.color];
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.06 }}
                  className="bg-white rounded-2xl border border-slate-200/70 p-5 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <p className="text-4xl font-bold text-slate-900 leading-none tracking-tight">{stat.value}</p>
                    <div className={`w-10 h-10 rounded-xl ${accent.soft} flex items-center justify-center`}>
                      <FontAwesomeIcon icon={stat.icon} className={`${accent.icon}`} />
                    </div>
                  </div>
                  <p className="text-sm font-medium text-slate-500 mt-3">{stat.label}</p>
                </motion.div>
              );
            })}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Pending Questionnaires */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-2xl shadow-sm border border-slate-200/70 flex flex-col"
            >
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2.5">
                  <span className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
                    <FontAwesomeIcon icon={faFileAlt} className="text-orange-500 text-sm" />
                  </span>
                  Wartende Fragebögen
                </h3>
                {pendingQuestionnaires.length > 0 && (
                  <span className="bg-orange-100 text-orange-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                    {pendingQuestionnaires.length}
                  </span>
                )}
              </div>

              <div className="max-h-96 overflow-y-auto">
                {pendingQuestionnaires.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-4xl text-gray-300 mb-3" />
                    <p>Keine wartenden Fragebögen</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {pendingQuestionnaires.map((item) => (
                      <div key={item._id} className="p-4 hover:bg-gray-50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-gray-900 truncate">
                              {item.title}
                            </h4>
                            <p className="text-sm text-gray-600 mt-1">
                              von <span className="font-medium">{item.supplier.name}</span>
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <FontAwesomeIcon icon={faCalendarAlt} />
                                {new Date(item.submittedAt).toLocaleDateString('de-DE')}
                              </span>
                              {item.ekArea && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                                  {item.ekArea}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-1 ml-4">
                            <button
                              onClick={() => handleApproveQuestionnaire(item._id)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                              title="Genehmigen"
                            >
                              <FontAwesomeIcon icon={faThumbsUp} />
                            </button>
                            <button
                              onClick={() => handleRejectQuestionnaire(item._id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              title="Ablehnen"
                            >
                              <FontAwesomeIcon icon={faThumbsDown} />
                            </button>
                            <button
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                              title="Ansehen"
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>

            {/* Pending Articles */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-2xl shadow-sm border border-slate-200/70 flex flex-col"
            >
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2.5">
                  <span className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
                    <FontAwesomeIcon icon={faBoxes} className="text-purple-500 text-sm" />
                  </span>
                  Wartende Artikel
                </h3>
                {pendingArticles.length > 0 && (
                  <span className="bg-purple-100 text-purple-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                    {pendingArticles.length}
                  </span>
                )}
              </div>

              <div className="max-h-96 overflow-y-auto">
                {pendingArticles.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-4xl text-gray-300 mb-3" />
                    <p>Keine wartenden Artikel</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {pendingArticles.map((item) => (
                      <div 
                        key={item._id} 
                        className="p-2 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => {/* Navigate to article */}}
                      >
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex-1 min-w-0 truncate">
                            <span className="font-medium text-gray-900">{item.title}</span>
                          </div>
                          <div className="flex items-center gap-3 ml-3 text-xs text-gray-500">
                            <span className="whitespace-nowrap">
                              {new Date(item.submittedAt).toLocaleDateString('de-DE')}
                            </span>
                            {item.ekArea && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs whitespace-nowrap">
                                {item.ekArea}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>

            {/* New Articles */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl shadow-sm border border-slate-200/70 flex flex-col"
            >
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2.5">
                  <span className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
                    <FontAwesomeIcon icon={faPlus} className="text-green-500 text-sm" />
                  </span>
                  Neue Artikel
                </h3>
                {newArticles.length > 0 && (
                  <span className="bg-green-100 text-green-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                    {newArticles.length}
                  </span>
                )}
              </div>

              <div className="max-h-96 overflow-y-auto">
                {newArticles.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-4xl text-gray-300 mb-3" />
                    <p>Keine neuen Artikel</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {newArticles.map((item) => (
                      <div 
                        key={item._id} 
                        className="p-2 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => {/* Navigate to article */}}
                      >
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex-1 min-w-0 truncate">
                            <span className="font-medium text-gray-900">{item.title}</span>
                          </div>
                          <div className="flex items-center gap-3 ml-3 text-xs text-gray-500">
                            <span className="whitespace-nowrap">
                              {new Date(item.submittedAt).toLocaleDateString('de-DE')}
                            </span>
                            {item.ekArea && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs whitespace-nowrap">
                                {item.ekArea}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
